import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle } from 'lucide-react';

import { Fragment } from 'react';

import { Button } from '@splitfiesta/ui';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText: string;
  cancelText: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
  /** After the leave transition, once the dialog has tried to hand focus back. */
  onAfterLeave?: () => void;
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  variant = 'default',
  isLoading = false,
  onAfterLeave,
}: ConfirmationDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const getConfirmButtonVariant = () => {
    switch (variant) {
      case 'danger':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const getIconColor = () => {
    switch (variant) {
      case 'danger':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      default:
        return 'text-blue-500';
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment} afterLeave={onAfterLeave}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300 motion-reduce:transition-none"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200 motion-reduce:transition-none"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md mx-4 sm:mx-0 transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start gap-3 mb-4 sm:items-center">
                  <div
                    className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-700`}
                  >
                    <AlertTriangle className={`h-5 w-5 ${getIconColor()}`} />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-base sm:text-lg font-medium leading-5 sm:leading-6 text-gray-900 dark:text-gray-100"
                  >
                    {title}
                  </Dialog.Title>
                </div>

                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {description}
                  </p>
                </div>

                <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    {cancelText}
                  </Button>
                  <Button
                    variant={getConfirmButtonVariant()}
                    onClick={handleConfirm}
                    disabled={isLoading}
                    className="w-full sm:w-auto min-h-[44px]"
                  >
                    {confirmText}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
